import { Button, Tooltip } from '@civilgrid/ui/components';
import type React from 'react';
import { createFeature } from '~/common/geoUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { copyGeometry, exportToKMZ } from '~/common/projectUtils';
import type { Project } from '~/common/types';
import type { TProject } from './common';

interface ExportButtonProps {
  children: React.ReactNode;
  tooltip: React.ReactNode;
  onClick: () => void;
  testId: string;
}

const ExportButton = ({ children, tooltip, onClick, testId }: ExportButtonProps) => {
  return (
    <Tooltip asChild text={tooltip}>
      <Button variant="secondary" onClick={onClick} data-testid={testId}>
        {children}
      </Button>
    </Tooltip>
  );
};

function openInGeojsonIo(project: TProject) {
  const fc: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [createFeature(project.geometry)],
  };

  const dataUrl = `data:application/json,${JSON.stringify(fc)}`;
  const url = `https://geojson.io/#data=${encodeURIComponent(dataUrl)}`;
  capturePosthogEvent(POSTHOG_EVENTS.civilsearchGeojsonOpened, { projectId: project.projectId });
  window.open(url, '_blank');
}

export default function ({ project }: { project: TProject }) {
  if (!project) return null;

  return (
    <div className="flex space-x-4">
      <ExportButton
        tooltip="Export to KML"
        // Handles a Typescript quick with types caused by TProject's Omit and ProjectWithDetails having the `[key: string]: any;` attribute.
        onClick={() => exportToKMZ(project as unknown as Project)}
        testId="admin-project-export-to-kmz-button"
      >
        Export to KMZ
      </ExportButton>

      <ExportButton
        tooltip="Copy geometry to clipboard"
        // Handles a Typescript quick with types caused by TProject's Omit and ProjectWithDetails having the `[key: string]: any;` attribute.
        onClick={() => copyGeometry(project as unknown as Project)}
        testId="admin-project-copy-as-geojson-button"
      >
        Copy as GeoJSON
      </ExportButton>

      <ExportButton
        tooltip="Open in geojson.io"
        onClick={() => openInGeojsonIo(project)}
        testId="admin-project-open-in-geojsonio-button"
      >
        Open in GeoJSON.io
      </ExportButton>
    </div>
  );
}
